import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyContext } from "./use-company-context";
import { calculatePlanStatus } from "./use-maintenance-plans";

export interface FleetReportFilters {
  startDate?: string;
  endDate?: string;
  vehicleIds?: string[];
  driverIds?: string[];
  costCenterIds?: string[];
}

export interface VehicleReportData {
  vehicle_id: string;
  vehicle_plate: string;
  vehicle_model: string;
  total_trips: number;
  total_distance: number;
  total_fuel_cost: number;
  total_maintenance_cost: number;
  total_cost: number;
  fuel_efficiency: number;
  utilization_rate: number;
}

export interface DriverReportData {
  driver_id: string;
  driver_name: string;
  total_trips: number;
  total_distance: number;
  average_speed: number;
  fuel_efficiency: number;
  safety_score: number;
  total_violations: number;
}

export interface MaintenanceReportData {
  work_order_id: string;
  order_number: number;
  vehicle_id: string;
  vehicle_plate: string;
  provider_name: string;
  maintenance_type: string;
  cost: number;
  opened_at: string;
  closed_at: string;
  status: string;
}

export interface CostCenterReportData {
  cost_center_id: string;
  cost_center_name: string;
  total_vehicles: number;
  total_drivers: number;
  total_cost: number;
  fuel_cost: number;
  maintenance_cost: number;
  cost_per_km: number;
}

// Vehicle Reports Hook
export function useVehicleReports(companyIdParam?: string, filters?: FleetReportFilters) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useVehicleReports] companyId:', companyId, 'filters:', filters);
  
  return useQuery({
    queryKey: ["vehicle-reports", companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];

      // Get all vehicles first (no inner join restriction)
      const { data: vehicles, error: vehiclesError } = await supabase
        .from("fleet_vehicles")
        .select(`
          id,
          plate,
          model,
          make
        `)
        .eq("company_id", companyId)
        .eq("status", "active");

      if (vehiclesError) throw vehiclesError;
      if (!vehicles || vehicles.length === 0) return [];

      // Get all related data separately
      const vehicleIds = vehicles.map(v => v.id);
      
      const [tripsResult, fuelLogsResult, workOrdersResult] = await Promise.all([
        supabase
          .from("fleet_trips")
          .select("id, vehicle_id, distance_actual_km, distance_planned_km, start_at, eta_start, end_at")
          .eq("company_id", companyId)
          .in("vehicle_id", vehicleIds),
        supabase
          .from("fleet_fuel_logs")
          .select("vehicle_id, liters, price_unit, occurred_at")
          .eq("company_id", companyId)
          .in("vehicle_id", vehicleIds),
        supabase
          .from("fleet_work_orders")
          .select("vehicle_id, total_cost, opened_at, status")
          .eq("company_id", companyId)
          .in("vehicle_id", vehicleIds)
      ]);

      const trips = tripsResult.data || [];
      const fuelLogs = fuelLogsResult.data || [];
      const workOrders = workOrdersResult.data || [];

      // Process and aggregate data for each vehicle
      return vehicles.map(vehicle => {
        const vehicleTrips = trips.filter(t => t.vehicle_id === vehicle.id);
        const vehicleFuelLogs = fuelLogs.filter(f => f.vehicle_id === vehicle.id);
        const vehicleWorkOrders = workOrders.filter(w => w.vehicle_id === vehicle.id);

        const totalDistance = vehicleTrips.reduce((sum, trip) => sum + (trip.distance_actual_km || trip.distance_planned_km || 0), 0);
        const totalFuelCost = vehicleFuelLogs.reduce((sum, log) => sum + ((log.liters || 0) * (log.price_unit || 0)), 0);
        const totalMaintenanceCost = vehicleWorkOrders.reduce((sum, order) => sum + (order.total_cost || 0), 0);
        const totalFuelLiters = vehicleFuelLogs.reduce((sum, log) => sum + (log.liters || 0), 0);

        return {
          vehicle_id: vehicle.id,
          vehicle_plate: vehicle.plate,
          vehicle_model: `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Modelo não informado',
          total_trips: vehicleTrips.length,
          total_distance: totalDistance,
          total_fuel_cost: totalFuelCost,
          total_maintenance_cost: totalMaintenanceCost,
          total_cost: totalFuelCost + totalMaintenanceCost,
          fuel_efficiency: totalDistance > 0 && totalFuelLiters > 0 ? totalDistance / totalFuelLiters : 0,
          utilization_rate: 0, // Calculate based on business logic
        } as VehicleReportData;
      });
    },
    enabled: !!companyId,
  });
}

// Driver Reports Hook
export function useDriverReports(companyIdParam?: string, filters?: FleetReportFilters) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useDriverReports] companyId:', companyId);
  
  return useQuery({
    queryKey: ["driver-reports", companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];

      // Get all drivers first (no restrictions)
      const { data: drivers, error: driversError } = await supabase
        .from("fleet_drivers")
        .select("id, name")
        .eq("company_id", companyId)
        .eq("status", "active");

      if (driversError) throw driversError;
      if (!drivers || drivers.length === 0) return [];

      // Get all related data separately
      const driverIds = drivers.map(d => d.id);
      
      const [tripsResult, assignmentsResult, alertsResult] = await Promise.all([
        supabase
          .from("fleet_trips")
          .select("id, driver_id, distance_actual_km, distance_planned_km, start_at, eta_start, end_at")
          .eq("company_id", companyId)
          .in("driver_id", driverIds),
        supabase
          .from("fleet_assignments")
          .select("driver_id, vehicle_id")
          .eq("company_id", companyId)
          .eq("status", "active")
          .in("driver_id", driverIds),
        supabase
          .from("fleet_alerts")
          .select("driver_id, priority")
          .eq("company_id", companyId)
          .eq("status", "active")
          .in("driver_id", driverIds)
      ]);

      const trips = tripsResult.data || [];
      const assignments = assignmentsResult.data || [];
      const alerts = alertsResult.data || [];

      return drivers.map(driver => {
        const driverTrips = trips.filter(t => t.driver_id === driver.id);
        const driverAlerts = alerts.filter(a => a.driver_id === driver.id);
        
        const totalDistance = driverTrips.reduce((sum, trip) => sum + (trip.distance_actual_km || trip.distance_planned_km || 0), 0);
        const totalViolations = driverAlerts.filter(a => a.priority === 'high' || a.priority === 'urgent').length;
        
        // Calculate basic safety score based on violations
        const safetyScore = Math.max(0, Math.min(100, 100 - (totalViolations * 10)));

        return {
          driver_id: driver.id,
          driver_name: driver.name || 'Nome não informado',
          total_trips: driverTrips.length,
          total_distance: totalDistance,
          average_speed: 0, // Would need trip point data for accurate calculation
          fuel_efficiency: 0, // Would need fuel consumption data
          safety_score: safetyScore,
          total_violations: totalViolations,
        } as DriverReportData;
      });
    },
    enabled: !!companyId,
  });
}

export function useMaintenanceReports(companyIdParam?: string, filters?: FleetReportFilters) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useMaintenanceReports] companyId:', companyId);
  
  return useQuery({
    queryKey: ["maintenance-reports", companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];

      console.log('[MaintenanceReports] Fetching work orders for company:', companyId);

      const { data: workOrders, error } = await supabase
        .from("fleet_work_orders")
      .select(`
        id,
        order_number,
        total_cost,
        opened_at,
        closed_at,
        status,
        description,
        vehicle_id,
        provider_id
      `)
        .eq("company_id", companyId);

      if (error) {
        console.error('[MaintenanceReports] Error fetching work orders:', error);
        throw error;
      }

      console.log(`[MaintenanceReports] Found ${workOrders?.length || 0} work orders`);

      // Get vehicle and provider data separately with explicit error handling
      const vehicleIds = [...new Set(workOrders?.map(order => order.vehicle_id).filter(Boolean))];
      const providerIds = [...new Set(workOrders?.map(order => order.provider_id).filter(Boolean))];

      console.log('[MaintenanceReports] Fetching vehicles:', vehicleIds.length);
      console.log('[MaintenanceReports] Fetching providers:', providerIds.length);

      const [vehiclesResult, providersResult] = await Promise.all([
        vehicleIds.length > 0 ? supabase
          .from("fleet_vehicles")
          .select("id, plate, make, model")
          .in("id", vehicleIds) : Promise.resolve({ data: [], error: null }),
        providerIds.length > 0 ? supabase
          .from("fleet_providers")
          .select("id, name")
          .in("id", providerIds) : Promise.resolve({ data: [], error: null })
      ]);

      if (vehiclesResult.error) {
        console.error('[MaintenanceReports] Error fetching vehicles:', vehiclesResult.error);
      }
      if (providersResult.error) {
        console.error('[MaintenanceReports] Error fetching providers:', providersResult.error);
      }

      const vehiclesMap = new Map<string, any>();
      const providersMap = new Map<string, any>();
      
      vehiclesResult.data?.forEach(v => {
        console.log(`[MaintenanceReports] Mapping vehicle: ${v.id} -> ${v.plate}`);
        vehiclesMap.set(v.id, v);
      });
      providersResult.data?.forEach(p => providersMap.set(p.id, p));

      console.log(`[MaintenanceReports] Vehicle map size: ${vehiclesMap.size}`);
      console.log(`[MaintenanceReports] Provider map size: ${providersMap.size}`);

      return workOrders?.map(order => {
        const vehicle = vehiclesMap.get(order.vehicle_id);
        const provider = providersMap.get(order.provider_id);
        
      const result = {
        work_order_id: order.id,
        order_number: order.order_number,
        vehicle_id: order.vehicle_id || "",
        vehicle_plate: vehicle?.plate || "Sem Placa",
        vehicle_model: vehicle ? `${vehicle.make || ''} ${vehicle.model || ''}`.trim() : "Modelo não informado",
        provider_name: provider?.name || "Sem Fornecedor",
        maintenance_type: order.description || "Manutenção Geral",
        cost: order.total_cost || 0,
        opened_at: order.opened_at || "",
        closed_at: order.closed_at || "",
        status: order.status || "open",
      };
        
        console.log(`[MaintenanceReports] Work order ${order.id.slice(0,8)}:`, {
          vehicle_id: order.vehicle_id,
          vehicle_plate: result.vehicle_plate
        });
        
        return result;
      }) || [];
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for pending maintenance plans
export function usePendingMaintenanceReports(companyIdParam?: string) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [usePendingMaintenanceReports] companyId:', companyId);
  
  return useQuery({
    queryKey: ["pending-maintenance-reports", companyId],
    queryFn: async () => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from("fleet_maint_plans")
        .select(`
          id,
          name,
          type,
          next_execution_date,
          next_execution_km,
          periodicity_days,
          periodicity_km,
          status,
          vehicle_id,
          created_at
        `)
        .eq("company_id", companyId)
        .eq("status", "active");

      if (error) throw error;

      // Get vehicle data
      const vehicleIds = [...new Set(data?.map(plan => plan.vehicle_id).filter(Boolean))];
      
      const vehiclesResult = vehicleIds.length > 0 ? await supabase
        .from("fleet_vehicles")
        .select("id, plate, make, model, odometer")
        .in("id", vehicleIds) : { data: [] };

      const vehiclesMap = new Map<string, any>();
      vehiclesResult.data?.forEach(v => vehiclesMap.set(v.id, v));

      // Get all work orders to calculate status
      const allWorkOrdersResult = await supabase
        .from("fleet_work_orders")
        .select("id, plan_id, status, closed_at")
        .eq("company_id", companyId);

      const allWorkOrders = allWorkOrdersResult.data || [];

      // Get last work order for each plan to calculate next execution date
      const workOrdersResult = data && data.length > 0 ? await supabase
        .from("fleet_work_orders")
        .select("plan_id, closed_at")
        .in("plan_id", data.map(p => p.id))
        .eq("status", "closed")
        .order("closed_at", { ascending: false }) : { data: [] };

      const lastWorkOrderMap = new Map<string, string>();
      workOrdersResult.data?.forEach(wo => {
        if (wo.plan_id && wo.closed_at && !lastWorkOrderMap.has(wo.plan_id)) {
          lastWorkOrderMap.set(wo.plan_id, wo.closed_at);
        }
      });

      const results = data?.map(plan => {
        const vehicle = vehiclesMap.get(plan.vehicle_id);
        const currentOdometer = vehicle?.odometer || 0;
        
        // Auto-calculate next execution if not defined
        let nextExecutionDate = plan.next_execution_date;
        let nextExecutionKm = plan.next_execution_km;
        let isDateCalculated = false;
        let isKmCalculated = false;

        // Calculate next execution date if not set but has periodicity
        if (!nextExecutionDate && plan.periodicity_days) {
          const lastWorkOrderDate = lastWorkOrderMap.get(plan.id);
          const baseDate = lastWorkOrderDate ? new Date(lastWorkOrderDate) : new Date(plan.created_at);
          const calculatedDate = new Date(baseDate);
          calculatedDate.setDate(calculatedDate.getDate() + plan.periodicity_days);
          nextExecutionDate = calculatedDate.toISOString();
          isDateCalculated = true;
        }

        // Calculate next execution km if not set but has periodicity
        if (!nextExecutionKm && plan.periodicity_km && currentOdometer) {
          nextExecutionKm = currentOdometer + plan.periodicity_km;
          isKmCalculated = true;
        }

        const kmUntilMaintenance = nextExecutionKm && currentOdometer 
          ? nextExecutionKm - currentOdometer 
          : null;

        // Calculate days until maintenance
        const daysUntilMaintenance = nextExecutionDate 
          ? Math.ceil((new Date(nextExecutionDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
          : null;

        return {
          plan_id: plan.id,
          plan_name: plan.name,
          maintenance_type: plan.type,
          vehicle_id: plan.vehicle_id || "",
          vehicle_plate: vehicle?.plate || "",
          vehicle_model: vehicle ? `${vehicle.make} ${vehicle.model}` : "",
          current_odometer: currentOdometer,
          next_execution_date: nextExecutionDate,
          next_execution_km: nextExecutionKm,
          km_until_maintenance: kmUntilMaintenance,
          days_until_maintenance: daysUntilMaintenance,
          periodicity_days: plan.periodicity_days,
          periodicity_km: plan.periodicity_km,
          status: plan.status,
          calculated_status: calculatePlanStatus(plan.status, plan.id, allWorkOrders),
          is_date_calculated: isDateCalculated,
          is_km_calculated: isKmCalculated,
        };
      }) || [];

      // Sort by urgency: overdue first, then critical, then normal
      return results.sort((a, b) => {
        const aOverdue = (a.days_until_maintenance !== null && a.days_until_maintenance < 0) ||
                        (a.km_until_maintenance !== null && a.km_until_maintenance < 0);
        const bOverdue = (b.days_until_maintenance !== null && b.days_until_maintenance < 0) ||
                        (b.km_until_maintenance !== null && b.km_until_maintenance < 0);
        
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;

        const aCritical = (a.days_until_maintenance !== null && a.days_until_maintenance <= 7) ||
                         (a.km_until_maintenance !== null && a.km_until_maintenance < 500);
        const bCritical = (b.days_until_maintenance !== null && b.days_until_maintenance <= 7) ||
                         (b.km_until_maintenance !== null && b.km_until_maintenance < 500);
        
        if (aCritical && !bCritical) return -1;
        if (!aCritical && bCritical) return 1;

        // Sort by days remaining (closest first)
        if (a.days_until_maintenance !== null && b.days_until_maintenance !== null) {
          return a.days_until_maintenance - b.days_until_maintenance;
        }

        return 0;
      });
    },
    enabled: !!companyId,
  });
}

// Cost Center Reports Hook
export function useCostCenterReports(companyIdParam?: string, filters?: FleetReportFilters) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useCostCenterReports] companyId:', companyId);
  
  return useQuery({
    queryKey: ["cost-center-reports", companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];

      // Get cost centers first
      const { data: costCenters, error: costCenterError } = await supabase
        .from("cost_centers")
        .select("id, name")
        .eq("company_id", companyId)
        .eq("status", "active");

      if (costCenterError) throw costCenterError;
      if (!costCenters) return [];

      // Get vehicles by cost center
      const { data: vehicles } = await supabase
        .from("fleet_vehicles")
        .select("id, cost_center_id")
        .eq("company_id", companyId)
        .eq("status", "active");

      // Get assignments by cost center  
      const { data: assignments } = await supabase
        .from("fleet_assignments")
        .select("driver_id, vehicle_id")
        .eq("company_id", companyId)
        .eq("status", "active");

      // Get fuel costs
      const { data: fuelLogs } = await supabase
        .from("fleet_fuel_logs")
        .select("vehicle_id, liters, price_unit")
        .eq("company_id", companyId);

      // Get maintenance costs
      const { data: workOrders } = await supabase
        .from("fleet_work_orders")
        .select("vehicle_id, total_cost")
        .eq("company_id", companyId);

      // Group data by cost center
      return costCenters.map(costCenter => {
        const centerVehicles = vehicles?.filter(v => v.cost_center_id === costCenter.id) || [];
        const vehicleIds = centerVehicles.map(v => v.id);
        
        const centerAssignments = assignments?.filter(a => 
          vehicleIds.includes(a.vehicle_id)
        ) || [];
        
        const centerFuelLogs = fuelLogs?.filter(f => vehicleIds.includes(f.vehicle_id)) || [];
        const centerWorkOrders = workOrders?.filter(w => vehicleIds.includes(w.vehicle_id)) || [];
        
        const totalFuelCost = centerFuelLogs.reduce((sum, log) => 
          sum + ((log.liters || 0) * (log.price_unit || 0)), 0
        );
        
        const totalMaintenanceCost = centerWorkOrders.reduce((sum, order) => 
          sum + (order.total_cost || 0), 0
        );
        
        const totalCost = totalFuelCost + totalMaintenanceCost;

        return {
          cost_center_id: costCenter.id,
          cost_center_name: costCenter.name,
          total_vehicles: centerVehicles.length,
          total_drivers: new Set(centerAssignments.map(a => a.driver_id)).size,
          total_cost: totalCost,
          fuel_cost: totalFuelCost,
          maintenance_cost: totalMaintenanceCost,
          cost_per_km: 0, // Would need trip data to calculate
        } as CostCenterReportData;
      });
    },
    enabled: !!companyId,
  });
}

// General Fleet Summary Hook
export function useFleetSummary(companyIdParam?: string) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useFleetSummary] companyId:', companyId);

  return useQuery({
    queryKey: ["fleet-summary", companyId],
    queryFn: async () => {
      if (!companyId) return null;

      const [vehiclesResult, driversResult, alertsResult] = await Promise.all([
        supabase
          .from("fleet_vehicles")
          .select("id, status")
          .eq("company_id", companyId),
        supabase
          .from("fleet_drivers")
          .select("id, status")
          .eq("company_id", companyId),
        supabase
          .from("fleet_alerts")
          .select("id, status, priority")
          .eq("company_id", companyId)
          .eq("status", "active"),
      ]);

      const vehicles = vehiclesResult.data || [];
      const drivers = driversResult.data || [];
      const alerts = alertsResult.data || [];

      return {
        total_vehicles: vehicles.length,
        active_vehicles: vehicles.filter(v => v.status === "active").length,
        total_drivers: drivers.length,
        active_drivers: drivers.filter(d => d.status === "active").length,
        active_alerts: alerts.length,
        critical_alerts: alerts.filter(a => a.priority === "high" || a.priority === "critical").length,
      };
    },
    enabled: !!companyId,
  });
}